import { type GlobalQuote, type Package } from '@qdrant/qdrant-cloud-public-api/booking/v1';
import { z } from 'zod';
import { millicentsToMonthlyPrice } from '@/components/Billing/helpers';
import { isFreeTierBookingPackage } from '@/utils/package-tier-checks';
import { getPricedEntityRates } from '../Clusters/ClusterSetup/helpers/getPricedEntityRates';
import { toPackageResourcesObject } from '../Clusters/ClusterSetup/helpers/toPackageResourcesObject';
import { CLOUD_PROVIDER_MAP } from '../Clusters/constants';
import { transformBytesToGigabytes } from '../Clusters/helpers';

const SCALAR_QUANTIZATION_DIVIDER = 4;
const BINARY_QUANTIZATION_DIVIDER = 32;
const PRODUCT_QUANTIZATION_DIVIDER = 64;
const TURBOQUANT_4_BIT_QUANTIZATION_DIVIDER = 8;
const TURBOQUANT_1_BIT_QUANTIZATION_DIVIDER = 32;

export const QUANTIZATIONS = [
  'Scalar',
  'Product',
  'Binary',
  'TurboQuant (4-bit)',
  'TurboQuant (1-bit)',
  'None',
] as const;
export type Quantization = (typeof QUANTIZATIONS)[number];
export const DEFAULT_QUANTIZATION: Quantization = 'None';

type PackageWithExtractedResources = Package & { memoryResource?: number; cpuResource?: number };

export const findPackageWithMostRAMAndLeastCPU = (packages: Package[]) => {
  let maxRamAmount = 0;
  let packageCPU: number | undefined;
  let packageWithMostRAM: Package | undefined = undefined;

  packages.forEach((pkg) => {
    const { memory: ramAmount, cpu: cpuAmount } = toPackageResourcesObject(pkg).getConfiguration();
    // Find the package with the most RAM and the lowest CPU
    if (ramAmount > maxRamAmount || (ramAmount === maxRamAmount && packageCPU && cpuAmount < packageCPU)) {
      maxRamAmount = ramAmount;
      packageWithMostRAM = pkg;
      packageCPU = cpuAmount;
    }
  });

  return packageWithMostRAM;
};

export const getPackageByMemory = (
  memory?: number,
  packages?: Package[],
): PackageWithExtractedResources | undefined => {
  if (!packages || !memory) {
    return undefined;
  }

  // Filter packages based on those that have a memory resource configuration matching or exceeding the required memory
  const suitablePackages = packages.reduce<PackageWithExtractedResources[]>((acc, pkg) => {
    const { memory: packageMemory, cpu: packageCpu } = toPackageResourcesObject(pkg).getConfiguration();

    // Only packages with memory >= required memory are suitable
    if (packageMemory >= memory) {
      acc.push({
        ...pkg,
        memoryResource: packageMemory,
        cpuResource: packageCpu,
      });
    }

    return acc;
  }, []);

  // If no suitable package was found it means the required memory exceeds the maximum memory available in the packages.
  // Return the package with the highest memory available
  if (suitablePackages.length === 0) {
    return findPackageWithMostRAMAndLeastCPU(packages);
  }

  // Group packages by memoryResource (to later find the package with the lowest CPU in each group)
  const groupedByMemory = suitablePackages.reduce<Record<number, PackageWithExtractedResources[]>>((groups, pkg) => {
    const ramGroup = pkg.memoryResource!;
    if (!groups[ramGroup]) {
      groups[ramGroup] = [];
    }
    groups[ramGroup].push(pkg);
    return groups;
  }, {});

  // For each group, find the package with the lowest CPU
  const lowestCPUPackages = Object.values(groupedByMemory).map((group) =>
    // All packages within groupedByMemory have cpuResource defined due to the filtering
    // First package is taken as the initial lowest CPU package by default.
    group.reduce((lowest, pkg) => (pkg.cpuResource! < lowest.cpuResource! ? pkg : lowest)),
  );

  // Sort by memoryResource to prioritize lower memory usage
  lowestCPUPackages.sort((a, b) => a.memoryResource! - b.memoryResource!);

  return lowestCPUPackages[0] || undefined;
};

export const getHighestMemoryAvailable = (packages: Package[]): number => {
  if (!packages.length) {
    return 1;
  }

  const memoryAmounts = packages.map((pkg) => {
    const { memory } = toPackageResourcesObject(pkg).getConfiguration();
    return memory;
  });

  return Math.max(...memoryAmounts, 1);
};

const VECTOR_SIZE_IN_BYTES = 4;
const USIZE = 4;
const METADATA_AND_OPTIMIZATION_MULTIPLIER = 1.5;

const getBaseRamGB = (vectors: number, dimension: number) =>
  transformBytesToGigabytes(vectors * dimension * VECTOR_SIZE_IN_BYTES * METADATA_AND_OPTIMIZATION_MULTIPLIER);

const getSparseRamGB = (sparseVectors: number, sparseElements: number) =>
  transformBytesToGigabytes(sparseVectors * sparseElements * (VECTOR_SIZE_IN_BYTES + USIZE));

// A Record makes the compiler reject a new quantization that has no divider.
const QUANTIZATION_DIVIDERS: Record<Quantization, number> = {
  Binary: BINARY_QUANTIZATION_DIVIDER,
  None: 1,
  Product: PRODUCT_QUANTIZATION_DIVIDER,
  Scalar: SCALAR_QUANTIZATION_DIVIDER,
  'TurboQuant (1-bit)': TURBOQUANT_1_BIT_QUANTIZATION_DIVIDER,
  'TurboQuant (4-bit)': TURBOQUANT_4_BIT_QUANTIZATION_DIVIDER,
};

const getQuantizedValue = (value: number, quantization: Quantization) => value / QUANTIZATION_DIVIDERS[quantization];

/**
 * Calculates the minimum required memory for a Qdrant cluster based on the provided
 * configuration (vectors, dimensions and if it's storage optimized).
 * See https://qdrant.tech/documentation/guides/capacity-planning/#basic-configuration for more details.
 */
export const getMinimumRequiredMemory = ({
  vectors,
  dimension,
  sparseVectors,
  sparseElements,
  storageOptimized,
  storageRAMCachePercentage,
  quantization,
}: {
  vectors: number;
  dimension: number;
  sparseVectors?: number;
  sparseElements?: number;
  storageOptimized?: boolean;
  storageRAMCachePercentage?: number;
  quantization: Quantization;
}) => {
  // Extra 50% is needed for metadata (indexes, point versions, etc.) as well as for temporary
  // segments constructed during the optimization process.
  let calculatedMemoryInGb = getBaseRamGB(vectors, dimension);
  calculatedMemoryInGb = getQuantizedValue(calculatedMemoryInGb, quantization);

  if (sparseVectors && sparseElements) {
    calculatedMemoryInGb += getSparseRamGB(sparseVectors, sparseElements);
  }

  if (storageOptimized && storageRAMCachePercentage) {
    calculatedMemoryInGb *= storageRAMCachePercentage / 100;
  }

  return Math.ceil(calculatedMemoryInGb);
};

export const getMinimumRequiredDisk = ({
  vectors,
  dimension,
  sparseVectors,
  sparseElements,
  quantization,
}: {
  vectors: number;
  dimension: number;
  sparseVectors?: number;
  sparseElements?: number;
  quantization: Quantization;
}) => {
  // Base disk equals the base RAM
  let baseRam = getBaseRamGB(vectors, dimension);
  if (sparseVectors && sparseElements) {
    baseRam += getSparseRamGB(sparseVectors, sparseElements);
  }
  let requiredDisk = baseRam;
  if (quantization !== 'None') {
    const quantizedRam = getQuantizedValue(baseRam, quantization);
    requiredDisk += quantizedRam;
  }

  return Math.ceil(requiredDisk);
};

export const MAX_VECTORS = 100_000_000_000;
export const MAX_DIMENSIONS = 65_536;
export const MAX_REPLICAS = 100;
export const DEFAULT_CALCULATOR_PROVIDER = CLOUD_PROVIDER_MAP.AWS;

const getTransformFunc = (maxValue: number) => (value: number) => {
  if (value < 0) {
    return 0;
  }

  if (value > maxValue) {
    return maxValue;
  }

  return value;
};

// Every bound carries its own message: these fields are rendered on the public calculator, and zod's
// default for a numeric bound reads "Too big: expected number to be <=65536".
export const calculatorSchema = z.object({
  dimension: z.coerce
    .number<number>()
    .int('Dimension must be an integer')
    .positive('Dimension must be positive')
    .max(MAX_DIMENSIONS, `Dimension must be at most ${MAX_DIMENSIONS}`)
    .transform(getTransformFunc(MAX_DIMENSIONS)),
  quantization: z.enum(QUANTIZATIONS, { error: 'Invalid quantization' }),
  replicas: z.coerce
    .number<number>()
    .int('Replicas must be an integer')
    .positive('Replicas must be positive')
    .max(MAX_REPLICAS, `Replicas must be at most ${MAX_REPLICAS}`)
    .transform(getTransformFunc(MAX_REPLICAS)),
  sparseElements: z.coerce
    .number<number>()
    .int('Sparse Vector Elements must be an integer')
    .min(0, 'Sparse Vector Elements cannot be negative')
    .max(MAX_DIMENSIONS, `Sparse Vector Elements must be at most ${MAX_DIMENSIONS}`)
    .transform(getTransformFunc(MAX_DIMENSIONS)),
  sparseVectors: z.coerce
    .number<number>()
    .int('Sparse Vectors must be an integer')
    .min(0, 'Sparse Vectors cannot be negative')
    .max(MAX_VECTORS, `Sparse Vectors must be at most ${MAX_VECTORS}`)
    .transform(getTransformFunc(MAX_VECTORS)),
  storageOptimized: z.boolean(),
  storageRAMCachePercentage: z.coerce
    .number<number>()
    .int('Storage RAM cache percentage must be an integer')
    .min(10, 'Storage RAM cache percentage must be at least 10')
    .max(100, 'Storage RAM cache percentage must be at most 100')
    .transform(getTransformFunc(35)),
  vectors: z.coerce
    .number<number>()
    .int('Vectors must be an integer')
    .positive('Vectors must be positive')
    .max(MAX_VECTORS, `Vectors must be at most ${MAX_VECTORS}`)
    .transform(getTransformFunc(MAX_VECTORS)),
});

export const getPackagesWithLowestCPUSortedByRAM = (packages: Package[]) => {
  const groupedPackages = new Map<number, Package>();

  packages.forEach((pkg: Package) => {
    const { memory: ramAmount, cpu: currentPackageCpu } = toPackageResourcesObject(pkg).getConfiguration();

    // Check if there's already a package for this RAM amount
    const existingPackage = groupedPackages.get(ramAmount);
    if (!existingPackage) {
      groupedPackages.set(ramAmount, pkg);
    } else {
      // Compare CPU of the current package with the stored one to replace if it's lower
      const { cpu: existingPackageCpu } = toPackageResourcesObject(existingPackage).getConfiguration();

      if (currentPackageCpu < existingPackageCpu) {
        groupedPackages.set(ramAmount, pkg);
      }
    }
  });

  // Sort packages by RAM
  const sortedPackages = new Map([...groupedPackages].toSorted((a, b) => a[0] - b[0]));
  // Return the packages with the lowest CPU for each RAM group
  return Array.from(sortedPackages.values());
};

type DBClusterConfiguration = {
  package: Package;
  nodes: number;
  extraDisk: number;
};

export type DBClusterConfigurationCandidate = DBClusterConfiguration & {
  spareRam: number;
  // Set only by enumerateCandidates, which already prices the candidate for its existence check.
  // A candidate built any other way (the unpriced highest-RAM fallback) has no browser rate to
  // reuse, so this stays optional rather than forcing every caller to supply one.
  monthlyTotal?: number;
};

/**
 * Enumerates one candidate per package: the smallest node count (from `minNodes` to `MAX_REPLICAS`)
 * covering `targetRam * replicationFactor`. A package with no listed `unitIntPricePerHour` is
 * skipped, same as today, so both the browser-priced and server-priced paths see the same pool.
 * Free-tier packages are skipped too: the calculator sizes paid clusters only.
 */
export function enumerateCandidates(
  packages: Package[],
  targetRam: number,
  targetDisk: number,
  replicationFactor: number,
): DBClusterConfigurationCandidate[] {
  const effectiveTargetRam = targetRam * replicationFactor;
  const effectiveTargetDisk = targetDisk * replicationFactor;
  const minNodes = replicationFactor > 1 ? 3 : 1;
  const candidates: DBClusterConfigurationCandidate[] = [];

  for (const pkg of packages) {
    if (isFreeTierBookingPackage(pkg)) {
      continue;
    }
    const { memory: ram, disk } = toPackageResourcesObject(pkg).getConfiguration();
    for (let nodes = minNodes; nodes <= MAX_REPLICAS; nodes++) {
      const totalRam = ram * nodes;
      // No rates means no pricing, so this package can't be used. Kept for the ranking step below
      // too, so it's priced exactly once per candidate rather than again during selection.
      const rates = getPricedEntityRates({ entity: pkg, multiplier: nodes });
      if (!rates) {
        continue;
      }

      // Check if the total RAM is sufficient
      if (totalRam >= effectiveTargetRam) {
        const totalAvailableDisk = disk * nodes;
        candidates.push({
          package: pkg,
          nodes,
          extraDisk:
            totalAvailableDisk >= effectiveTargetDisk ? 0 : Math.ceil((effectiveTargetDisk - disk * nodes) / nodes),
          spareRam: totalRam - effectiveTargetRam,
          monthlyTotal: rates.monthlyTotal,
        });

        // Break out of the loop as we don't need more nodes for this RAM package
        break;
      }
    }
  }

  return candidates;
}

/**
 * Selects the cheapest candidate, preferring fewer nodes within 15% of the cheapest price and,
 * on a price tie, fewer nodes then less spare RAM. `monthlyPackageCost` is pluggable so the same
 * rule ranks browser-computed rates or server-quoted prices.
 */
export function selectOptimalConfiguration(
  candidates: DBClusterConfigurationCandidate[],
  monthlyPackageCost: (candidate: DBClusterConfigurationCandidate) => number,
): DBClusterConfiguration | null {
  let bestConfiguration: DBClusterConfiguration | null = null;
  let leastSpareRam = Infinity;
  let cheapestMonthlyPrice = Infinity;
  let leastNodes = Infinity;

  for (const candidate of candidates) {
    const { nodes, spareRam } = candidate;
    const monthlyTotal = monthlyPackageCost(candidate);
    if (
      monthlyTotal < cheapestMonthlyPrice ||
      (nodes < leastNodes && monthlyTotal < cheapestMonthlyPrice * 1.15) ||
      (monthlyTotal === cheapestMonthlyPrice && (nodes < leastNodes || spareRam < leastSpareRam))
    ) {
      // Update the best configuration if this one is the most efficient cost/spare ram-wise
      leastNodes = nodes;
      leastSpareRam = spareRam;
      cheapestMonthlyPrice = monthlyTotal;
      bestConfiguration = { package: candidate.package, nodes, extraDisk: candidate.extraDisk };
    }
  }

  return bestConfiguration;
}

export function findOptimalDBClusterConfiguration(
  packages: Package[],
  targetRam: number,
  targetDisk: number,
  replicationFactor: number,
): DBClusterConfiguration | null {
  const candidates = enumerateCandidates(packages, targetRam, targetDisk, replicationFactor);
  // enumerateCandidates already priced every candidate once for its existence check; reuse it
  // instead of pricing again here.
  return selectOptimalConfiguration(candidates, (candidate) => candidate.monthlyTotal!);
}

const candidateKey = (candidate: { package: Package; nodes: number }) => `${candidate.package.id}:${candidate.nodes}`;

/**
 * Selects the optimal configuration from server-priced `candidates` and their index-aligned
 * `quotes` (from `GetGlobalQuotes`). Returns `null` while the quotes aren't safe to rank yet:
 * `isPlaceholderData` means they're still the previous candidate list's quotes (react-query keeps
 * showing them while a new request is in flight), and a length mismatch means the response hasn't
 * caught up with the candidate list either — ranking either against the current `candidates` would
 * pair the wrong price with the wrong configuration.
 */
export function selectFromGlobalQuotes(
  candidates: DBClusterConfigurationCandidate[],
  quotes: GlobalQuote[],
  isPlaceholderData: boolean,
): { configuration: DBClusterConfiguration; quote?: GlobalQuote } | null {
  if (isPlaceholderData || candidates.length === 0 || quotes.length !== candidates.length) {
    return null;
  }
  const quotesByCandidate = new Map(candidates.map((candidate, index) => [candidateKey(candidate), quotes[index]]));
  const configuration = selectOptimalConfiguration(candidates, (candidate) => {
    const quote = quotesByCandidate.get(candidateKey(candidate));
    return quote?.package ? millicentsToMonthlyPrice(quote.package.pricePerHour) : Infinity;
  });
  if (!configuration) {
    return null;
  }

  // Only reached when no quote has a package breakdown: nothing priced, so nothing to recommend.
  const quote = quotesByCandidate.get(candidateKey(configuration));
  if (!quote?.package) {
    return null;
  }
  return { configuration, quote };
}

/**
 * Every param is attacker-controlled, so no value may reject the whole URL: `.catch` swallows a
 * garbage param, `.optional()` lets an absent one through as `undefined`, and `.default` covers a
 * key that has neither — zod 4 rejects a missing key before `.catch` runs.
 */
export const calculatorSearchSchema = z.object({
  dimension: z.coerce
    .number()
    .int()
    .min(0)
    .optional()
    .catch(0)
    .pipe(z.number().max(MAX_DIMENSIONS).optional().catch(MAX_DIMENSIONS)),
  provider: z
    .enum([CLOUD_PROVIDER_MAP.AWS, CLOUD_PROVIDER_MAP.GCP, CLOUD_PROVIDER_MAP.AZURE])
    .default(DEFAULT_CALCULATOR_PROVIDER)
    .catch(DEFAULT_CALCULATOR_PROVIDER),
  quantization: z.enum(QUANTIZATIONS).default(DEFAULT_QUANTIZATION).catch(DEFAULT_QUANTIZATION),
  region: z.string().optional(),
  replicas: z.coerce
    .number()
    .int()
    .min(1)
    .optional()
    .catch(1)
    .pipe(z.number().max(MAX_REPLICAS).optional().catch(MAX_REPLICAS)),
  sparseElements: z.coerce
    .number()
    .int()
    .min(0)
    .optional()
    .catch(0)
    .pipe(z.number().max(MAX_DIMENSIONS).optional().catch(MAX_DIMENSIONS)),
  sparseVectors: z.coerce
    .number()
    .int()
    .min(0)
    .optional()
    .catch(0)
    .pipe(z.number().max(MAX_VECTORS).optional().catch(MAX_VECTORS)),
  storageOptimized: z.coerce.boolean().default(false).catch(false),
  storageRAMCachePercentage: z.coerce
    .number()
    .int('Storage RAM cache percentage must be an integer')
    .min(10)
    .max(100)
    .optional()
    .catch(35)
    .pipe(z.number().max(100).optional().catch(100)),
  vectors: z.coerce
    .number()
    .int()
    .min(0)
    .optional()
    .catch(0)
    .pipe(z.number().max(MAX_VECTORS).optional().catch(MAX_VECTORS)),
});

export type CalculatorSearchParams = z.infer<typeof calculatorSearchSchema>;
